import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext.tsx';
import saccoLoanService from "@saccoLoanService";

const LOANS_KEY = 'sacco_loans';
const REPAYMENTS_KEY = 'sacco_loan_repayments';

/**
 * Custom hook to fetch sacco loans linked to a specific membership ID.
 *
 * @param {string} membershipId - The unique identifier for the user's membership in the sacco.
 * @return {object} Returns the query object from the useQuery hook, containing the state and methods for managing the data-fetching process.
 */
export function useFetchSaccoLoans(membershipId: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [LOANS_KEY, membershipId],
    queryFn: async () => {
      if (!user) {
        throw Error('User is not authenticated');
      }
      return await saccoLoanService.fetchLoans(user!.id, membershipId);
    },
    enabled: !!user && !!membershipId,
  });
}

/**
 * Creates and manages the mutation for creating a sacco loan and its corresponding repayment schedule.
 *
 * @param {string} membershipId - The membership ID associated with the loan.
 * @return {MutationObserver<SaccoLoan>} A mutation observer that handles the creation of the sacco loan and repayment schedule.
 */
export function useCreateSaccoLoan(membershipId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: SaccoLoanCreateRequest) => {
      return await saccoLoanService.createLoan(user!.id, membershipId, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [LOANS_KEY, membershipId] });
      qc.invalidateQueries({ queryKey: [REPAYMENTS_KEY] });
    },
  });
}

/**
 * A custom hook that provides functionality to update a Sacco loan associated with a specific membership.
 *
 * @param {string} membershipId - The unique identifier of the Sacco membership.
 * @return {UseMutationResult} The mutation result object that includes methods and states for updating the loan.
 */
export function useUpdateSaccoLoan(membershipId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: SaccoLoanUpdateRequest) => {
      return await saccoLoanService.updateLoan(req);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [LOANS_KEY, membershipId] }),
  });
}

/**
 * Custom hook to delete a Sacco loan based on its ID. This hook provides a mutation function
 * to delete the loan and invalidates relevant query caches upon successful deletion.
 *
 * @param {string} membershipId - The membership ID associated with the loan and used for caching purposes.
 * @return {UseMutationResult} A mutation object from React Query, providing methods and state for the delete operation.
 */
export function useDeleteSaccoLoan(membershipId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return await saccoLoanService.deleteLoan(id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [LOANS_KEY, membershipId] });
      qc.invalidateQueries({ queryKey: [REPAYMENTS_KEY] });
    },
  });
}
